import {
  createIndirectTaxFilingV2Document,
  IIndirectTaxFilingV2,
  IIndirectTaxFilingV2UpdateParams,
  IndirectTaxFilingV2DocumentType,
  SalesTaxFilingStatusEnum,
  submitIndirectTaxFilingV2,
  updateIndirectTaxFilingV2,
  useIndirectTaxFilingV2,
  useIndirectTaxFilingV2DocumentsWithFiles,
  useIndirectTaxFilingV2Prepayments,
} from '@commenda/api/indirect-tax/filings';
import { uploadSalesTaxFile } from '@commenda/api/new-sales-tax/files/upload';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FileUploadStatus,
  InputErrorMessage,
  SingleFileUploader,
  TimelineLegacy,
  TTimelineItemStatusLegacy,
  useTimelineLegacy,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { FileBasic } from '@commenda/types/src/file/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegacyDocumentType } from '@prisma/client';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { useNotification } from '../../../../hooks/useNotification';
import FilingInputRow from '../../../../sales-tax/filing/filing-review/FilingInputRow';

enum FilingCompletionSteps {
  FILING_INFORMATION = 'FILING_INFORMATION',
  UPLOADS = 'UPLOADS',
}

// Mirrors the V1 (sales-tax) acknowledgement/payment upload constraints.
const CONFIRMATION_FILE_CONFIG = {
  maxSize: 100 * 1024 * 1024, // 100MB
  acceptedTypes: {
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
  },
  message: 'PNG, JPG & PDF accepted (max. 100 MB)',
};

// Zod schema — ported verbatim from the V1 FilingConfirmationModal. File
// presence is tracked in component state (not the schema) since the V2 upload
// flow stages the file then attaches it on submit.
const FilingFormSchema = z.object({
  filingDate: z.string().trim().min(1, 'Filing date is required'),
  taxPaid: z.string().trim().min(1, 'Tax paid is required'),
  taxLiability: z.string().trim().min(1, 'Tax liability is required'),
  taxableSales: z.string().trim().min(1, 'Taxable sales is required'),
  nonTaxableSales: z.string().trim().min(1, 'Non-taxable sales is required'),
  marketplaceSales: z.string().trim().min(1, 'Marketplace sales is required'),
  exemptSales: z.string().trim().min(1, 'Exempt sales is required'),
  grossSales: z.string().trim().min(1, 'Gross sales is required'),
  numberOfTransactions: z
    .string()
    .trim()
    .min(1, 'Number of transactions is required'),

  filingConfirmationNumber: z
    .string()
    .trim()
    .min(1, 'Filing confirmation number is required'),
  paymentConfirmationNumber: z.string().trim().optional(),

  // Adjustments
  prepayment: z.string().trim().optional(),
  priorPayment: z.string().trim().optional(),
  retailDeliveryFee: z.string().trim().optional(),
  jurisdictionCredit: z.string().trim().optional(),
  marketplacePayment: z.string().trim().optional(),
  vendorDiscount: z.string().trim().optional(),
  rounding: z.string().trim().optional(),
  penalty: z.string().trim().optional(),
  interest: z.string().trim().optional(),
});

type FilingFormData = z.infer<typeof FilingFormSchema>;

// V2 amounts are flat camelCase fields in MAJOR currency units (see
// IIndirectTaxFilingV2Amounts) — no metadata nesting and no extra scaling.
const getInitialFormValues = (
  filing?: IIndirectTaxFilingV2
): FilingFormData => {
  const taxable = filing?.taxableAmount ?? filing?.estimatedTaxableAmount ?? 0;
  const nonTaxable =
    filing?.nonTaxableAmount ?? filing?.estimatedNonTaxableAmount ?? 0;
  const exempt = filing?.exemptAmount ?? filing?.estimatedExemptAmount ?? 0;
  const marketplace =
    filing?.marketplaceAmount ?? filing?.estimatedMarketplaceAmount ?? 0;

  return {
    filingDate: filing?.filedAt || '',
    taxPaid: filing?.taxPaid?.toString() || '',
    taxLiability: (
      filing?.taxLiability ??
      filing?.estimatedTaxCollected ??
      0
    ).toFixed(2),
    taxableSales: taxable.toFixed(2),
    nonTaxableSales: nonTaxable.toFixed(2),
    exemptSales: exempt.toFixed(2),
    marketplaceSales: marketplace.toFixed(2),
    // V2 has no stored gross field; derive it from the components, matching
    // how the V1 modal surfaced gross_amount as a read-only roll-up.
    grossSales: (taxable + nonTaxable + exempt + marketplace).toFixed(2),
    numberOfTransactions: filing?.filingTransactionsCount?.toString() || '',
    prepayment: filing?.prepayment?.toString() || '',
    priorPayment: filing?.priorPayment?.toString() || '',
    retailDeliveryFee: filing?.retailDeliveryFee?.toString() || '',
    jurisdictionCredit: filing?.jurisdictionIssuedCredit?.toString() || '',
    marketplacePayment: filing?.marketplacePayment?.toString() || '0',
    vendorDiscount: filing?.vendorDiscount?.toString() || '',
    rounding: filing?.rounding?.toString() || '',
    penalty: filing?.penalty?.toString() || '',
    interest: filing?.interest?.toString() || '',
    filingConfirmationNumber: filing?.filingConfirmationNumber || '',
    paymentConfirmationNumber: filing?.paymentConfirmationNumber || '',
  };
};

interface IndirectTaxFilingConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingId: string;
  corporationId: number;
  companyId: number;
  onComplete: () => void;
  viewMode?: boolean;
}

export function IndirectTaxFilingConfirmationModal({
  isOpen,
  onClose,
  filingId,
  corporationId,
  companyId,
  onComplete,
  viewMode = false,
}: IndirectTaxFilingConfirmationModalProps) {
  const canEdit = !viewMode;
  const { setNotification } = useNotification();

  const { filing, mutate: refetchFiling } = useIndirectTaxFilingV2(
    filingId,
    isOpen ? { corporationId } : null
  );
  const {
    confirmationDocuments,
    paymentDocuments,
    mutate: refetchDocuments,
  } = useIndirectTaxFilingV2DocumentsWithFiles(
    isOpen ? filingId : null,
    isOpen ? corporationId : null
  );
  const { prepayments } = useIndirectTaxFilingV2Prepayments(
    filingId,
    corporationId,
    { enabled: isOpen }
  );

  const isEditingFiledFiling =
    !viewMode && filing?.status === SalesTaxFilingStatusEnum.FILED;

  // Newly-staged files for this session. SingleFileUploader pushes the file to
  // S3 and gives us a FileBasic; we register + attach it on submit (mirrors the
  // VAT path in IndirectTaxFilingVatConfirmModal).
  const [filingConfirmationFile, setFilingConfirmationFile] =
    useState<FileBasic | null>(null);
  const [paymentConfirmationFile, setPaymentConfirmationFile] =
    useState<FileBasic | null>(null);
  const [filingConfirmationFileError, setFilingConfirmationFileError] =
    useState('');
  const [paymentConfirmationFileError, setPaymentConfirmationFileError] =
    useState('');
  const [isUploadingFilingConfirmation, setIsUploadingFilingConfirmation] =
    useState(false);
  const [isUploadingPaymentConfirmation, setIsUploadingPaymentConfirmation] =
    useState(false);
  const isUploading =
    isUploadingFilingConfirmation || isUploadingPaymentConfirmation;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FilingFormData>({
    resolver: zodResolver(FilingFormSchema),
    defaultValues: getInitialFormValues(filing),
  });

  useEffect(() => {
    if (isOpen) {
      reset(getInitialFormValues(filing));
    }
  }, [isOpen, filing?.id, reset]);

  const {
    steps,
    setActiveStep,
    activeStep,
    markStepAsCompleted,
    resetFurtherSteps,
  } = useTimelineLegacy({
    items: [
      {
        id: FilingCompletionSteps.FILING_INFORMATION,
        text: 'Filing Information',
        status: TTimelineItemStatusLegacy.ACTIVE,
        isCompleted: viewMode,
        disabled: false,
        isHidden: false,
      },
      {
        id: FilingCompletionSteps.UPLOADS,
        text: 'Uploads',
        status: TTimelineItemStatusLegacy.NOT_STARTED,
        isCompleted: viewMode,
        disabled: false,
        isHidden: false,
      },
    ],
  });

  // Clear staged files and rewind the timeline when the modal closes so the
  // next open starts from the first step.
  useEffect(() => {
    if (!isOpen) {
      setFilingConfirmationFile(null);
      setPaymentConfirmationFile(null);
      setFilingConfirmationFileError('');
      setPaymentConfirmationFileError('');
      if (viewMode) setActiveStep(0);
      else resetFurtherSteps(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isEditingFiledFiling) return;
    markStepAsCompleted(steps.length - 1);
    setActiveStep(0);
  }, [isOpen, isEditingFiledFiling]);

  const handleInputChange = (field: keyof FilingFormData, value: string) => {
    setValue(field, value);
  };

  const toNumber = (field: keyof FilingFormData) => {
    const value = watch(field);
    return value ? Number(value) : undefined;
  };

  // Persist the editable filing fields. Maps the V1 snake_case payload onto the
  // V2 camelCase update DTO (IIndirectTaxFilingV2UpdateParams).
  const updateFilingRecord = async (params?: { filedAt: boolean }) => {
    const payload: IIndirectTaxFilingV2UpdateParams = {
      corporationId,
      filingConfirmationNumber: watch('filingConfirmationNumber') || undefined,
      paymentConfirmationNumber:
        watch('paymentConfirmationNumber') || undefined,
      prepayment: toNumber('prepayment'),
      priorPayment: toNumber('priorPayment'),
      retailDeliveryFee: toNumber('retailDeliveryFee'),
      jurisdictionIssuedCredit: toNumber('jurisdictionCredit'),
      marketplacePayment: toNumber('marketplacePayment'),
      vendorDiscount: toNumber('vendorDiscount'),
      rounding: toNumber('rounding'),
      penalty: toNumber('penalty'),
      interest: toNumber('interest'),
      taxPaid: toNumber('taxPaid'),
      taxLiability: toNumber('taxLiability'),
      taxableAmount: toNumber('taxableSales'),
      nonTaxableAmount: toNumber('nonTaxableSales'),
      exemptAmount: toNumber('exemptSales'),
      marketplaceAmount: toNumber('marketplaceSales'),
      ...(params?.filedAt && watch('filingDate')
        ? { filedAt: watch('filingDate') }
        : {}),
    };

    const response = await updateIndirectTaxFilingV2({
      filingId,
      params: payload,
    });

    if (response.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'Filing updated',
        message: 'Filing updated successfully',
      });
      await refetchFiling();
      return true;
    }

    setNotification({
      show: true,
      valid: false,
      title: 'Failed to update filing',
      message: response.error,
    });
    return false;
  };

  // Register a staged file with sales-tax-api and attach it to the filing as the
  // given document type. Returns true on success.
  const attachDocument = async (
    file: FileBasic,
    documentType: IndirectTaxFilingV2DocumentType
  ) => {
    const uploaded = await uploadSalesTaxFile({
      file,
      corporation_id: corporationId,
    });
    if (!uploaded.success || !uploaded.data?.data?.id) {
      return uploaded.error ?? 'Failed to upload file';
    }

    const created = await createIndirectTaxFilingV2Document({
      filingId,
      params: {
        corporationId,
        fileId: uploaded.data.data.id,
        documentType,
        fileName: file.fileName,
        fileUrl: file.fileUrl,
      },
    });
    if (!created.success) {
      return created.error ?? 'Failed to attach file';
    }
    return null;
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);

    if (filingConfirmationFile) {
      const error = await attachDocument(
        filingConfirmationFile,
        IndirectTaxFilingV2DocumentType.FILING_CONFIRMATION
      );
      if (error) {
        setFilingConfirmationFileError(error);
        setIsSubmitting(false);
        return;
      }
    }

    if (paymentConfirmationFile) {
      const error = await attachDocument(
        paymentConfirmationFile,
        IndirectTaxFilingV2DocumentType.PAYMENT_CONFIRMATION
      );
      if (error) {
        setPaymentConfirmationFileError(error);
        setIsSubmitting(false);
        return;
      }
    }

    const submitResponse = await submitIndirectTaxFilingV2({
      filingId,
      corporationId,
    });
    setIsSubmitting(false);

    if (submitResponse.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'Filing submitted',
        message: 'Filing submitted successfully',
      });
      mixpanel.track(MIXPANEL_EVENTS.FILING_MARKED_COMPLETE, {
        surface_area: MIXPANEL_SURFACE_AREA.AGENT_APP,
      });
      await refetchDocuments();
      onComplete();
      onClose();
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to submit filing',
        message: submitResponse.error,
      });
    }
  };

  const handleSaveEdits = async () => {
    setIsSubmitting(true);

    if (filingConfirmationFile) {
      const error = await attachDocument(
        filingConfirmationFile,
        IndirectTaxFilingV2DocumentType.FILING_CONFIRMATION
      );
      if (error) {
        setFilingConfirmationFileError(error);
        setIsSubmitting(false);
        return;
      }
    }

    if (paymentConfirmationFile) {
      const error = await attachDocument(
        paymentConfirmationFile,
        IndirectTaxFilingV2DocumentType.PAYMENT_CONFIRMATION
      );
      if (error) {
        setPaymentConfirmationFileError(error);
        setIsSubmitting(false);
        return;
      }
    }

    const isUpdated = await updateFilingRecord({ filedAt: true });
    setIsSubmitting(false);
    if (!isUpdated) return;

    await refetchDocuments();
    onComplete();
    onClose();
  };

  const handleNext = async () => {
    // Persist filingDate at the step boundary so async refreshes/remounts cannot
    // rehydrate this modal with a blank filed_at value (matches V1 behavior).
    const isUpdated = await updateFilingRecord({ filedAt: true });
    if (!isUpdated) return;

    if (activeStep.id === FilingCompletionSteps.FILING_INFORMATION) {
      markStepAsCompleted(0);
      setActiveStep(1);
    } else {
      await handleFormSubmit();
    }
  };

  const isFilingInformationComplete = () =>
    Boolean(
      watch('filingDate') &&
      watch('taxPaid') &&
      watch('taxLiability') &&
      watch('taxableSales') &&
      watch('nonTaxableSales')
    );

  const hasFilingConfirmation =
    Boolean(filingConfirmationFile) || confirmationDocuments.length > 0;

  const renderPrepaymentLogs = () => {
    if (prepayments.length === 0) {
      return (
        <div className="text-sm text-text-tertiary border border-border-primary rounded-xl p-4">
          No prepayment records available for this filing.
        </div>
      );
    }

    const formatDue = (date: string | null) =>
      date
        ? NonTimezoneSensitiveDate.fromISO8601String(date).format('MMM DD')
        : '—';
    const money = (value: number | null) => `$${(value ?? 0).toLocaleString()}`;

    return (
      <div className="border border-border-primary rounded-xl overflow-hidden">
        <div className="flex bg-bg-tertiary txt-small-medium text-text-quaternary">
          <div className="py-lg px-xl flex-1">Filing</div>
          <div className="py-lg px-xl w-[120px]">Due</div>
          <div className="py-lg px-xl w-[140px] text-right">Tax liability</div>
          <div className="py-lg px-xl w-[120px] text-right">Tax paid</div>
          <div className="py-lg px-xl w-[120px] text-right">Prepayment</div>
        </div>
        {prepayments.map((p) => (
          <div
            key={p.id}
            className="flex border-t border-border-primary txt-small-regular text-text-primary"
          >
            <div className="py-lg px-xl flex-1">{p.formTitle}</div>
            <div className="py-lg px-xl w-[120px]">
              {formatDue(p.filingDueDate)}
            </div>
            <div className="py-lg px-xl w-[140px] text-right">
              {money(p.taxLiability)}
            </div>
            <div className="py-lg px-xl w-[120px] text-right">
              {money(p.taxPaid)}
            </div>
            <div className="py-lg px-xl w-[120px] text-right">
              {money(p.prepayment)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderFilingInformation = () => (
    <div className="flex flex-col gap-6xl">
      <div className="flex flex-col gap-3xl">
        {!viewMode && !isEditingFiledFiling && (
          <Alert
            variant="warning"
            title="Complete Filing Information"
            description={
              <span className="txt-small-regular">
                Please enter the filing details below after successfully
                submitting the tax return to the state and use numbers from the
                state return.
              </span>
            }
          />
        )}
        {isEditingFiledFiling && (
          <Alert
            variant="info"
            title="Edit filing information"
            description={
              <span className="txt-small-regular">
                Update the filing details below and save your changes. The
                filing stays filed — saving does not resubmit it to the state.
              </span>
            }
          />
        )}
        <div className="flex flex-col gap-md">
          <h2 className="text-lg font-semibold mb-2xl">Filing summary</h2>
          <div className="border border-border-primary rounded-xl overflow-hidden">
            <FilingInputRow
              title="Filing date"
              value={watch('filingDate')}
              onChange={(value) => handleInputChange('filingDate', value)}
              className="border-b"
              type="date"
              error={errors.filingDate?.message}
              required
              editable={canEdit}
            />
            <FilingInputRow
              title="Tax Liability"
              value={watch('taxLiability')}
              onChange={(value) => handleInputChange('taxLiability', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.taxLiability?.message}
              required
              editable={false}
            />
            <FilingInputRow
              title="Tax paid"
              value={watch('taxPaid')}
              onChange={(value) => handleInputChange('taxPaid', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.taxPaid?.message}
              required
              editable={canEdit}
            />
            <FilingInputRow
              title="Gross sales"
              value={watch('grossSales')}
              onChange={(value) => handleInputChange('grossSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.grossSales?.message}
              required
              editable={false}
            />
            <FilingInputRow
              title="Taxable sales"
              value={watch('taxableSales')}
              onChange={(value) => handleInputChange('taxableSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.taxableSales?.message}
              required
              editable={false}
            />
            <FilingInputRow
              title="Non-taxable sales"
              value={watch('nonTaxableSales')}
              onChange={(value) => handleInputChange('nonTaxableSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.nonTaxableSales?.message}
              required
              editable={false}
            />
            <FilingInputRow
              title="Exempt sales"
              value={watch('exemptSales')}
              onChange={(value) => handleInputChange('exemptSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.exemptSales?.message}
              required
              editable={false}
            />
            <FilingInputRow
              title="Marketplace sales"
              value={watch('marketplaceSales')}
              onChange={(value) => handleInputChange('marketplaceSales', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.marketplaceSales?.message}
              required
              editable={false}
            />
            <FilingInputRow
              title="Number of transactions"
              value={watch('numberOfTransactions')}
              onChange={(value) =>
                handleInputChange('numberOfTransactions', value)
              }
              placeholder="Enter number"
              type="number"
              error={errors.numberOfTransactions?.message}
              required
              editable={false}
            />
          </div>
        </div>

        <div className="flex flex-col gap-md">
          <h2 className="text-lg font-semibold mb-2xl">Filing prepayment logs</h2>
          {renderPrepaymentLogs()}
        </div>

        <div className="flex flex-col gap-md">
          <h2 className="text-lg font-semibold mb-2xl">Adjustments</h2>
          <div className="border border-border-primary rounded-xl overflow-hidden">
            <FilingInputRow
              title="Prepayment"
              value={watch('prepayment')}
              onChange={(value) => handleInputChange('prepayment', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.prepayment?.message}
              hint="+ve only"
              editable={canEdit}
            />
            <FilingInputRow
              title="Prior payment"
              value={watch('priorPayment')}
              onChange={(value) => handleInputChange('priorPayment', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.priorPayment?.message}
              hint="+ve only"
              editable={canEdit}
            />
            <FilingInputRow
              title="Retail delivery fee"
              value={watch('retailDeliveryFee')}
              onChange={(value) =>
                handleInputChange('retailDeliveryFee', value)
              }
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.retailDeliveryFee?.message}
              hint="+ve only"
              editable={canEdit}
            />
            <FilingInputRow
              title="Jurisdiction credit"
              value={watch('jurisdictionCredit')}
              onChange={(value) =>
                handleInputChange('jurisdictionCredit', value)
              }
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.jurisdictionCredit?.message}
              hint="+ve only"
              editable={canEdit}
            />
            <FilingInputRow
              title="Vendor discount"
              value={watch('vendorDiscount')}
              onChange={(value) => handleInputChange('vendorDiscount', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.vendorDiscount?.message}
              hint="+ve only"
              editable={canEdit}
            />
            <FilingInputRow
              title="Rounding"
              value={watch('rounding')}
              onChange={(value) => handleInputChange('rounding', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.rounding?.message}
              hint="+ve/-ve"
              editable={canEdit}
            />
            <FilingInputRow
              title="Penalty"
              value={watch('penalty')}
              onChange={(value) => handleInputChange('penalty', value)}
              placeholder="Enter amount"
              className="border-b"
              prefix="$"
              type="number"
              error={errors.penalty?.message}
              hint="+ve only"
              editable={canEdit}
            />
            <FilingInputRow
              title="Interest"
              value={watch('interest')}
              onChange={(value) => handleInputChange('interest', value)}
              placeholder="Enter amount"
              prefix="$"
              type="number"
              error={errors.interest?.message}
              hint="+ve only"
              editable={canEdit}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderUploads = () => (
    <div className="flex flex-col gap-6">
      <h2 className="text-xl font-semibold">Uploads</h2>

      <div className="flex flex-col gap-sm">
        <div className="txt-small-medium text-text-secondary">
          Filing acknowledgement
        </div>
        {confirmationDocuments.length > 0 ? (
          <p className="txt-small-regular text-text-tertiary">
            {canEdit ? 'Already attached: ' : ''}
            <a
              href={confirmationDocuments[0].signedUrl}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {confirmationDocuments[0].fileName}
            </a>
          </p>
        ) : (
          viewMode && (
            <p className="txt-small-regular text-text-tertiary">
              No file uploaded
            </p>
          )
        )}
        {canEdit && (
          <>
            <SingleFileUploader
              companyId={companyId}
              file={filingConfirmationFile}
              setFile={setFilingConfirmationFile}
              documentType={LegacyDocumentType.Other}
              maxSize={CONFIRMATION_FILE_CONFIG.maxSize}
              accept={CONFIRMATION_FILE_CONFIG.acceptedTypes}
              message={CONFIRMATION_FILE_CONFIG.message}
              error={filingConfirmationFileError}
              setError={setFilingConfirmationFileError}
              onError={() =>
                setFilingConfirmationFileError(
                  'File failed to upload. Please try again.'
                )
              }
              onUploadStatusChange={(status) =>
                setIsUploadingFilingConfirmation(
                  status === FileUploadStatus.UPLOADING
                )
              }
            />
            {filingConfirmationFileError && (
              <InputErrorMessage message={filingConfirmationFileError} />
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-sm">
        <div className="txt-small-medium text-text-secondary">
          Payment confirmation
        </div>
        {paymentDocuments.length > 0 ? (
          <p className="txt-small-regular text-text-tertiary">
            {canEdit ? 'Already attached: ' : ''}
            <a
              href={paymentDocuments[0].signedUrl}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {paymentDocuments[0].fileName}
            </a>
          </p>
        ) : (
          viewMode && (
            <p className="txt-small-regular text-text-tertiary">
              No file uploaded
            </p>
          )
        )}
        {canEdit && (
          <>
            <SingleFileUploader
              companyId={companyId}
              file={paymentConfirmationFile}
              setFile={setPaymentConfirmationFile}
              documentType={LegacyDocumentType.Other}
              maxSize={CONFIRMATION_FILE_CONFIG.maxSize}
              accept={CONFIRMATION_FILE_CONFIG.acceptedTypes}
              message={CONFIRMATION_FILE_CONFIG.message}
              error={paymentConfirmationFileError}
              setError={setPaymentConfirmationFileError}
              onError={() =>
                setPaymentConfirmationFileError(
                  'File failed to upload. Please try again.'
                )
              }
              onUploadStatusChange={(status) =>
                setIsUploadingPaymentConfirmation(
                  status === FileUploadStatus.UPLOADING
                )
              }
            />
            {paymentConfirmationFileError && (
              <InputErrorMessage message={paymentConfirmationFileError} />
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-sm">
        <div className="txt-small-medium text-text-secondary">
          Filing confirmation
        </div>
        <div className="border border-border-primary rounded-xl overflow-hidden">
          <FilingInputRow
            title="Acknowledgement"
            value={watch('filingConfirmationNumber')}
            onChange={(value) =>
              handleInputChange('filingConfirmationNumber', value)
            }
            placeholder="Enter acknowledgement number"
            type="text"
            className="border-b"
            error={errors.filingConfirmationNumber?.message}
            required
            editable={canEdit}
          />
          <FilingInputRow
            title="Payment confirmation"
            value={watch('paymentConfirmationNumber')}
            onChange={(value) =>
              handleInputChange('paymentConfirmationNumber', value)
            }
            placeholder="Enter payment confirmation number"
            type="text"
            error={errors.paymentConfirmationNumber?.message}
            editable={canEdit}
          />
        </div>
      </div>
    </div>
  );

  const isUploadsStep = activeStep.id === FilingCompletionSteps.UPLOADS;

  const canSaveEdits =
    !isSubmitting &&
    !isUploading &&
    isFilingInformationComplete() &&
    hasFilingConfirmation &&
    Boolean(watch('filingConfirmationNumber'));

  return (
    <Dialog open={isOpen} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent
        className="flex flex-col min-w-[90vw] h-[90vh]"
        preventCloseOnOutsideClick
        hideCloseIcon
      >
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex gap-2 items-center">
            {viewMode ? 'View' : isEditingFiledFiling ? 'Edit' : 'Complete'}{' '}
            {filing?.formTitle}
            {filing?.jurisdictionName && (
              <span className="txt-small-regular text-text-tertiary">
                | {filing.jurisdictionName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex h-full">
            <div className="w-[280px] border-r border-border-primary bg-bg-tertiary p-6">
              <TimelineLegacy
                steps={steps}
                setActiveStep={setActiveStep}
                forceDisable={isSubmitting || isUploading}
              />
            </div>
            <div className="flex-1 p-8 overflow-y-auto">
              {activeStep.id === FilingCompletionSteps.FILING_INFORMATION &&
                renderFilingInformation()}
              {isUploadsStep && renderUploads()}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} size="sm">
            {viewMode ? 'Close' : 'Cancel'}
          </Button>
          {isEditingFiledFiling &&
            (isUploadsStep ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setActiveStep(0)}
                  disabled={isSubmitting || isUploading}
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveEdits}
                  disabled={!canSaveEdits}
                >
                  {isSubmitting ? 'Saving...' : 'Save changes'}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                onClick={() => setActiveStep(1)}
                disabled={isUploading || !isFilingInformationComplete()}
              >
                Next
              </Button>
            ))}
          {!viewMode && !isEditingFiledFiling && (
            <Button
              size="sm"
              onClick={handleNext}
              disabled={
                isSubmitting ||
                isUploading ||
                (!isUploadsStep && !isFilingInformationComplete()) ||
                (isUploadsStep &&
                  (!hasFilingConfirmation ||
                    !isFilingInformationComplete() ||
                    !watch('filingConfirmationNumber')))
              }
            >
              {isUploadsStep
                ? isSubmitting
                  ? 'Submitting...'
                  : 'Complete'
                : 'Next'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
